import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import './EntityMentionList.css';

/* The character picker for script cue lines.
   Same keyboard contract and the same styling as SlashCommandList and
   EntityMentionList (up/down/enter, scroll the selection into view) so every
   suggestion popup in the editor behaves and looks alike. */
const ScriptCharacterList = forwardRef((props, ref) => {
	const [selectedIndex, setSelectedIndex] = useState(0);
	const listRef = useRef(null);

	const selectItem = (index) => {
		const item = props.items[index];
		if (item) props.command({ name: item.name });
	};

	useEffect(() => setSelectedIndex(0), [props.items]);

	// Keyboard selection must stay inside the scroll window — Enter firing on a
	// row the user cannot see was one of the phase-2 review bugs.
	useEffect(() => {
		listRef.current?.children[selectedIndex]?.scrollIntoView({ block: 'nearest' });
	}, [selectedIndex]);

	useImperativeHandle(ref, () => ({
		onKeyDown: ({ event }) => {
			/* With no matches this popup is hidden, so it must decline every key
			   it would otherwise claim — above all Return, which the script keymap
			   needs in order to advance a newly-introduced character into their
			   first line of dialogue. */
			if (!props.items.length) return false;

			if (event.key === 'ArrowUp') {
				setSelectedIndex((i) => (i + props.items.length - 1) % props.items.length);
				return true;
			}
			if (event.key === 'ArrowDown') {
				setSelectedIndex((i) => (i + 1) % props.items.length);
				return true;
			}
			if (event.key === 'Enter') {
				selectItem(selectedIndex);
				return true;
			}
			return false;
		},
	}));

	if (!props.items.length) return null;

	return (
		<div className='entity-list' ref={listRef}>
			{props.items.map((item, index) => (
				<button
					type='button'
					key={item.name}
					className={`entity-item ${index === selectedIndex ? 'is-selected' : ''}`}
					onMouseEnter={() => setSelectedIndex(index)}
					onClick={() => selectItem(index)}>
					<span className='entity-item__icon' aria-hidden='true'>
						<i className='fa-solid fa-fw fa-user'></i>
					</span>
					<span className='entity-item__text'>
						<span className='entity-item__label'>{item.name}</span>
					</span>
					<span className='entity-item__type'>
						{item.count === 1 ? '1 cue' : `${item.count} cues`}
					</span>
				</button>
			))}
		</div>
	);
});

ScriptCharacterList.displayName = 'ScriptCharacterList';

export default ScriptCharacterList;
